import {publishOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {getCopilotChatPolicyVersion, isCopilotChatAppAvailable} from '@github-ui/copilot-chat-helpers/constants'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CopilotIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import styles from './PanelToggleButton.module.css'

import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'

export function OpenCopilotChatPanelButton({
  size = 'small',
  variant = 'default',
  className,
}: {
  size?: 'small' | 'medium'
  variant?: 'invisible' | 'default'
  className?: string
}) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.CopilotChat)
  const actions = useDockedPanelActions()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const buttonAriaLabel = isDockedPanelOpen ? 'Close Copilot panel' : 'Open Copilot panel'

  return (
    <div className={`d-flex flex-items-center${className ? ` ${className}` : ''}`}>
      <ErrorBoundary
        boundaryName="OpenCopilotChatPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The Copilot panel cannot currently be opened."
            icon={CopilotIcon}
            size={size}
            variant={variant}
          />
        }
      >
        <IconButton
          className={styles.panelToggle}
          aria-label={buttonAriaLabel}
          aria-expanded={isDockedPanelOpen}
          icon={CopilotIcon}
          size={size}
          variant={variant}
          onClick={() => {
            // Emit the payload-aware routing decision so dashboards classify the
            // surface that will consume this source-less open event.
            sendPullRequestAnalyticsEvent('copilot_chat_side_panel.open', 'COPILOT_CHAT_SIDE_PANEL_BUTTON', {
              chat_version: getCopilotChatPolicyVersion({
                chatAppEmbeddedEnabled: isCopilotChatAppAvailable(),
              }),
            })
            if (isDockedPanelOpen) {
              actions.closePanel()
            } else {
              // Fire the standard open event so chat state is ready before the
              // provider mounts the panel DOM. This avoids stale thread content.
              publishOpenCopilotChat({intent: CopilotChatIntents.conversation})
            }
          }}
        />
      </ErrorBoundary>
    </div>
  )
}
